import { useEffect, useState } from "react";
import type { Authorization, ClockDay, TimeStatus } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";

interface Props {
  authorization: Authorization;
}

/** "3u05", as in the prototype. */
export function duration(from: string, to: Date): string {
  const minutes = Math.max(0, Math.round((to.getTime() - new Date(from).getTime()) / 60_000));
  return `${String(Math.floor(minutes / 60))}u${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * Beheer › Tijdsregistratie, as in the prototype: the owner switches clocking on or off for the shop;
 * when it is on, today's in, out and duration per person. Adjusting hours and the overviews per week
 * or month follow (besluiten 28/09/2026: after the final legal rules of 2027).
 */
export function TimeScreen({ authorization }: Props) {
  const { api, report, toast, time, timeChanged, staffRevision } = useTill();
  const [today, setToday] = useState<ClockDay[] | null>(null);
  const [now, setNow] = useState(() => new Date());
  const text = t.time.admin;
  const owner = authorization.staff.role === "owner";
  const on = time?.on ?? true;

  useEffect(() => {
    let current = true;
    api
      .request<{ on: boolean; today: ClockDay[] }>("GET", "/device/time/today", {
        staffToken: authorization.token,
        retry: true,
      })
      .then((answer) => {
        if (current) setToday(answer.today);
      })
      .catch(report);
    return () => {
      current = false;
    };
    // the staff and their clocking change live (time.updated raises staffRevision)
  }, [api, authorization.token, report, staffRevision]);

  // the duration of whoever is clocked in grows
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const toggle = async (next: boolean) => {
    const before = time;
    // the checkbox follows the tap at once; the Klok button too
    timeChanged({ on: next, clockedIn: time?.clockedIn ?? 0 });
    try {
      await api.request<TimeStatus>("PUT", "/device/time/settings", {
        staffToken: authorization.token,
        body: { on: next },
      });
      toast(next ? text.turnedOn : text.turnedOff);
    } catch (error) {
      if (before) timeChanged(before);
      report(error);
    }
  };

  return (
    <div className="panel admin-section time-admin">
      <h2>{text.title}</h2>
      <div className="zbox">
        <label className="inl tmon">
          <input
            type="checkbox"
            checked={on}
            disabled={!owner}
            onChange={(event) => void toggle(event.target.checked)}
          />{" "}
          {text.use}
        </label>
        <p className="note tmnote">
          {owner ? "" : text.ownerOnly}
          {text.note}
        </p>
      </div>
      {on ? (
        <>
          <h3>{text.today}</h3>
          <div className="tablewrap">
            <table className="stafftbl">
              <thead>
                <tr>
                  <th>{text.columns.name}</th>
                  <th>{text.columns.in}</th>
                  <th>{text.columns.out}</th>
                  <th className="r">{text.columns.duration}</th>
                  <th>{text.columns.status}</th>
                </tr>
              </thead>
              <tbody>
                {(today ?? []).map((row) => (
                  <tr key={row.staffId}>
                    <td>
                      <b>{row.name}</b>
                    </td>
                    <td className="num">{row.clockIn ? formatTime(row.clockIn) : "—"}</td>
                    <td className="num">{row.clockOut ? formatTime(row.clockOut) : "—"}</td>
                    <td className="r num">
                      {row.clockIn ? duration(row.clockIn, row.clockOut ? new Date(row.clockOut) : now) : ""}
                    </td>
                    <td>
                      {row.status === "in" ? (
                        <span className="stp on">{text.clockedIn}</span>
                      ) : row.status === "done" ? (
                        <span className="stp off">{text.clockedOut}</span>
                      ) : (
                        <span className="note small">{text.notIn}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">{text.later}</p>
        </>
      ) : (
        <p className="note">{text.off}</p>
      )}
    </div>
  );
}
